/**
 * The competitions list (wave 12, card 3d9c00e6): `GET /api/v1/analytics/competitions`
 * and its `.csv` (`modules/competition_performance`, whose README is the contract).
 * `analytics.read` to read, `exports.create` for the CSV.
 *
 * Every competition in scope, one row each, with the period's commerce (the Overview's
 * read model), measured behaviour and the advertising columns. The API sorts and pages
 * across every competition, so the page never sorts a page of rows itself.
 *
 * Every figure the API cannot give arrives as `null` and stays `null` here: a column it
 * names in `not_available` reads "Not live yet" with the reason, any other `null` is
 * undefined for that row (no orders, so no AOV) and reads "—". Never 0 in its place.
 * Money stays in integer minor units, as on the wire: `meta.currency`'s, except the
 * ticket price, which is in the competition's own `ticketPriceCurrency`.
 */

import type { GlobalFilters } from '@/lib/filters'

import { csvFilename, DEFAULT_SORT_ORDER, SORT_ORDERS, type CsvFile, type SortOrder } from './list'
import { overviewSearch, type OverviewMetaOut } from './overview'
import type { Envelope } from './types'

/** `sort` values the API takes (`definitions.py` `Sort`); the default is `revenue`. */
export const COMPETITION_SORTS = [
  'revenue',
  'revenue_net',
  'orders',
  'buyers',
  'aov',
  'tickets_sold_in_range',
  'tickets_sold_lifetime',
  'total_ticket_capacity',
  'sell_through',
  'new_customers',
  'returning_customers',
  'views',
  'unique_viewers',
  'selector_opens',
  'tickets_selected',
  'add_to_baskets',
  'conversion_rate',
  'name',
  'end_at',
] as const
export type CompetitionSort = (typeof COMPETITION_SORTS)[number]

export { DEFAULT_SORT_ORDER, SORT_ORDERS, type SortOrder }

export const DEFAULT_COMPETITION_SORT: CompetitionSort = 'revenue'

/**
 * The statuses the Overview counts as on sale (`overview/definitions.py`
 * `ACTIVE_STATUSES`), which `active` on every row and `by_status` entry reads. The
 * "Active competitions" KPI's drill-down (`?status=active`) filters by them.
 */
export const ACTIVE_STATUSES: readonly string[] = ['live', 'open']

/** Why a column has no value (`definitions.py` `NotAvailable`). */
export const UNAVAILABLE_REASONS = ['before_first_event', 'refund_not_itemised', 'needs_ad_platform'] as const
export type CompetitionUnavailableReason = (typeof UNAVAILABLE_REASONS)[number]

export interface CompetitionRow {
  // identity: the competition as the website synced it
  competitionId: string
  externalCompetitionId: string
  websiteId: string
  name: string
  /** The website's own word (`live`, `drawn`, `closed`…), as stored. */
  status: string
  /** `status` is one of `ACTIVE_STATUSES`. */
  active: boolean
  category: string | null
  imageUrl: string | null
  productUrl: string | null
  /** In `ticketPriceCurrency`, the competition's own: not converted. */
  ticketPriceMinor: number | null
  ticketPriceCurrency: string
  startAt: string | null
  endAt: string | null
  drawAt: string | null
  totalTicketCapacity: number | null
  // commerce: orders completed in the period, in `meta.currency`
  revenueGrossMinor: number
  /** Null when a refund in the period cannot be placed on this competition. */
  revenueNetMinor: number | null
  orders: number
  buyers: number
  /** Null without an order. */
  aovMinor: number | null
  ticketsSoldInRange: number
  ticketsSoldLifetime: number | null
  /** A fraction; null without a capacity or a lifetime figure. */
  sellThrough: number | null
  newCustomers: number
  returningCustomers: number
  // behaviour: measured sessions' events in the period; null before the website's first event
  views: number | null
  uniqueViewers: number | null
  selectorOpens: number | null
  ticketsSelected: number | null
  addToBaskets: number | null
  /** A fraction; also null with no viewing session. */
  conversionRate: number | null
  // advertising: null until an ad platform's spend reaches the Suite
  adSpendMinor: number | null
  attributedRevenueMinor: number | null
  cpaMinor: number | null
  roas: number | null
}

/** A row field that may be named in `notAvailable`. */
export type CompetitionField = keyof CompetitionRow

export interface CompetitionStatusCount {
  status: string
  count: number
  active: boolean
}

/** When behaviour is measured from: the API's `coverage`. */
export interface CompetitionsCoverage {
  /** The earliest behavioural event in scope before the period ends; null when none. */
  firstEventAt: string | null
  /** The later of the period's start and `firstEventAt`; null when nothing is measured. */
  measuredFrom: string | null
  /** A sentence to show beside the behaviour columns. */
  note: string
}

export interface CompetitionsList {
  /** This page, in `sort` / `order`. */
  rows: CompetitionRow[]
  /** Every competition matching the filters, before paging. */
  total: number
  /** Competitions in scope by status, under the category filter but not the status one. */
  byStatus: CompetitionStatusCount[]
  sort: CompetitionSort
  order: SortOrder
  limit: number
  offset: number
  /** Each field that is null on a returned row for a reason, and the reason. */
  notAvailable: Partial<Record<CompetitionField, CompetitionUnavailableReason>>
  coverage: CompetitionsCoverage
  /** The currency every money column but the ticket price is in: `meta.currency`. */
  currency: string
  /** The tenant's timezone, which its days (and the period) are in. */
  timezone: string
}

export interface CompetitionsQuery {
  /** The global filters: the period, the website and the environments. */
  filters: GlobalFilters
  /** The website's own status words; empty or absent: every status. */
  statuses?: readonly string[]
  /** Categories; empty or absent: every category. */
  categories?: readonly string[]
  sort?: CompetitionSort
  order?: SortOrder
  /** 1–200; the JSON route only. */
  limit?: number
  /** The JSON route only. */
  offset?: number
}

/** The JSON route's largest `limit`. */
export const MAX_COMPETITIONS_PAGE = 200


/** The most rows one CSV carries (`definitions.py` `CSV_MAX_ROWS`). */
export const CSV_MAX_ROWS = 5000

export interface CompetitionsApi {
  competitions(query: CompetitionsQuery): Promise<Envelope<CompetitionsList>>
  /** Every matching competition (up to `CSV_MAX_ROWS`) as CSV (`exports.create`); a refusal rejects with the API's error. */
  competitionsCsv(query: CompetitionsQuery): Promise<CsvFile>
}

// --- wire shapes (snake_case, as the API sends them) -------------------------

export interface CompetitionRowOut {
  competition_id: string
  external_competition_id: string
  website_id: string
  name: string
  status: string
  active: boolean
  category: string | null
  image_url: string | null
  product_url: string | null
  ticket_price_minor: number | null
  ticket_price_currency: string
  start_at: string | null
  end_at: string | null
  draw_at: string | null
  total_ticket_capacity: number | null
  revenue_gross_minor: number
  revenue_net_minor: number | null
  orders: number
  buyers: number
  aov_minor: number | null
  tickets_sold_in_range: number
  tickets_sold_lifetime: number | null
  sell_through: number | null
  new_customers: number
  returning_customers: number
  views: number | null
  unique_viewers: number | null
  selector_opens: number | null
  tickets_selected: number | null
  add_to_baskets: number | null
  conversion_rate: number | null
  ad_spend_minor: number | null
  attributed_revenue_minor: number | null
  cpa_minor: number | null
  roas: number | null
}

export interface CompetitionsOut {
  data: {
    rows: CompetitionRowOut[]
    total: number
    by_status: CompetitionStatusCount[]
    sort: CompetitionSort
    order: SortOrder
    limit: number
    offset: number
    not_available: Record<string, string>
    coverage: { first_event_at: string | null; measured_from: string | null; note: string }
  }
  meta: OverviewMetaOut
}

/** Wire column → row field: the row mapper, and how `not_available`'s keys become the row's. */
const FIELDS = {
  competition_id: 'competitionId',
  external_competition_id: 'externalCompetitionId',
  website_id: 'websiteId',
  name: 'name',
  status: 'status',
  active: 'active',
  category: 'category',
  image_url: 'imageUrl',
  product_url: 'productUrl',
  ticket_price_minor: 'ticketPriceMinor',
  ticket_price_currency: 'ticketPriceCurrency',
  start_at: 'startAt',
  end_at: 'endAt',
  draw_at: 'drawAt',
  total_ticket_capacity: 'totalTicketCapacity',
  revenue_gross_minor: 'revenueGrossMinor',
  revenue_net_minor: 'revenueNetMinor',
  orders: 'orders',
  buyers: 'buyers',
  aov_minor: 'aovMinor',
  tickets_sold_in_range: 'ticketsSoldInRange',
  tickets_sold_lifetime: 'ticketsSoldLifetime',
  sell_through: 'sellThrough',
  new_customers: 'newCustomers',
  returning_customers: 'returningCustomers',
  views: 'views',
  unique_viewers: 'uniqueViewers',
  selector_opens: 'selectorOpens',
  tickets_selected: 'ticketsSelected',
  add_to_baskets: 'addToBaskets',
  conversion_rate: 'conversionRate',
  ad_spend_minor: 'adSpendMinor',
  attributed_revenue_minor: 'attributedRevenueMinor',
  cpa_minor: 'cpaMinor',
  roas: 'roas',
} as const satisfies Record<keyof CompetitionRowOut, CompetitionField>

/** The row `FIELDS` builds: each row field with its wire column's type. */
type RowFromFields = { [K in keyof CompetitionRowOut as (typeof FIELDS)[K]]: CompetitionRowOut[K] }

function isReason(value: string): value is CompetitionUnavailableReason {
  return (UNAVAILABLE_REASONS as readonly string[]).includes(value)
}

function isWireField(key: string): key is keyof CompetitionRowOut {
  return Object.hasOwn(FIELDS, key)
}

/**
 * One row, renamed by `FIELDS`. Returning `RowFromFields` as a `CompetitionRow` is
 * checked by the compiler: a row field `FIELDS` does not produce, or produces with
 * another type, fails the build, so a column is added in one place.
 */
export function competitionRowFromWire(row: CompetitionRowOut): CompetitionRow {
  const mapped = Object.fromEntries(
    (Object.keys(FIELDS) as (keyof CompetitionRowOut)[]).map((wire) => [FIELDS[wire], row[wire]]),
  ) as RowFromFields
  return mapped
}

/**
 * `not_available` keyed by row field. A column or reason this client does not know
 * (one a later API adds) is left out rather than guessed at: its `null`s then read "—".
 * The competition detail's summary (the list's row) reads its own the same way.
 */
export function notAvailableFromWire(body: Record<string, string>): CompetitionsList['notAvailable'] {
  const out: CompetitionsList['notAvailable'] = {}
  for (const [key, reason] of Object.entries(body)) {
    if (isWireField(key) && isReason(reason)) out[FIELDS[key]] = reason
  }
  return out
}

export function competitionsFromWire(body: CompetitionsOut): CompetitionsList {
  const { data } = body
  return {
    rows: data.rows.map(competitionRowFromWire),
    total: data.total,
    byStatus: data.by_status.map(({ status, count, active }) => ({ status, count, active })),
    sort: data.sort,
    order: data.order,
    limit: data.limit,
    offset: data.offset,
    notAvailable: notAvailableFromWire(data.not_available),
    coverage: {
      firstEventAt: data.coverage.first_event_at,
      measuredFrom: data.coverage.measured_from,
      note: data.coverage.note,
    },
    currency: body.meta.currency,
    timezone: body.meta.timezone,
  }
}


/**
 * The query string both routes read: the Overview's scope (`from`/`to`, `website_id`,
 * the environments), then the filters, the sort and, for the JSON route, the page. The
 * CSV is asked with the same query less its page, so the file is the list on screen.
 */
export function competitionsSearch(query: CompetitionsQuery, now: Date): string {
  const params = new URLSearchParams(overviewSearch(query.filters, now))
  for (const status of query.statuses ?? []) params.append('status', status)
  for (const category of query.categories ?? []) params.append('category', category)
  if (query.sort) params.set('sort', query.sort)
  if (query.order) params.set('order', query.order)
  if (query.limit !== undefined) params.set('limit', String(query.limit))
  if (query.offset !== undefined) params.set('offset', String(query.offset))
  return params.toString()
}

/** The name the API gives the file (`competitions-<from>-to-<to>.csv`), for an answer that names none. */
export function competitionsCsvFilename(from: string, to: string): string {
  return csvFilename('competitions', from, to)
}
